import { Container, Row, Col, size } from '@everlywell/leaves';
import AnchorLink from 'components/AnchorLink';
import MeasuredItemsModal from 'components/MeasuredItemsModal';
import PDPSwiperWrapper from 'components/PDP/ProducSwiperWrapper';
import Pricing from 'components/Pricing';
import { Theme, Themable } from 'components/Themable';
import React, { useEffect, useState, useRef, useContext } from 'react';
import { useInView } from 'react-intersection-observer';
import 'intersection-observer';
import ewAnalytics from 'utils/analytics';
import { ANALYTICS } from 'utils/constants/analytics';
import { PDP } from 'utils/constants/dataTest';
import { sanitize } from 'utils/domHelpers';
import { generateKey } from 'utils/helpers';
import { Promo } from 'utils/helpers/activePromoHelpers';
import { ComparisonModalContext } from 'utils/helpers/pdpContexts';

import { SubscriptionOptionType } from '../../../containers/PDP/HeroContainer';
import lightsLeft from '../../../images/themes/Winter2020/lights-left.svg';
import lightsRight from '../../../images/themes/Winter2020/lights-right.svg';
import PurchaseControls from './PurchaseControls';
import star from './star.svg';
import * as S from './styles';

export type PDPHeroProps = {
  productId: number;
  name: string;
  price: string;
  memberPrice?: string;
  memberHeroCopy?: string;
  membershipVariantId: number;
  activeCredits?: number;
  descriptionHeader: string;
  description: string;
  averageRating: number;
  reviewCount: number;
  sliderImages: { image: string | React.ReactNode; imageAlt: string }[];
  collectionMethods: Array<{ iconUrl: string; name: string }>;
  measurements: Array<{ iconUrl: string; name: string }>;
  subscriptionOptions: SubscriptionOptionType[];
  onSubscriptionOptionChange: (e: React.FormEvent<HTMLInputElement>) => void;
  onAddToCartClick: (
    e: React.FormEvent<HTMLButtonElement>,
    callback?: () => void,
  ) => void;
  onExpressPayClick: (callback: () => void) => void;
  onPurchaseTypeChange: (e: React.FormEvent<HTMLInputElement>) => void;
  onQuantityPickerChange: (e: React.FormEvent<HTMLInputElement>) => void;
  purchaseTypeRadioButtons: Array<{ label: string; name: string }>;
  activeSubscriptionOption: SubscriptionOptionType;
  activePurchaseType: string;
  subscribeSelected: boolean;
  topCallout?: string;
  bottomCallout?: string;
  descriptionCallout?: string;
  modalTitle?: string;
  modalButtonText?: string;
  listOfMeasuredItems?: Array<{
    title: string;
    image: string;
    items: string[];
  }>;
  hideSubscription?: boolean;
  heroSubscriptionFaqUrl?: string;
  onFrequencyTriggerClick: () => void;
  onSlide: () => void;
  ctaSubheadline: string;
  quantity: number;
  isOutOfStock: boolean;
  consultAvailable?: boolean;
  consultAvailableText: string;
  consultIcon: string;
  promo?: Promo;
  promotionable?: boolean;
  onSlideChangeFromSwipe?: () => void;
  onNextArrowClick?: () => void;
  maxQuantity?: number;
  onPrevArrowClick?: () => void;
  onBulletClick?: (clickedIndex: number) => void;
  regionalRestrictionApplies?: boolean;
};

export const PDPHero = ({
  productId,
  name,
  price,
  memberPrice,
  memberHeroCopy,
  membershipVariantId,
  activeCredits,
  descriptionHeader,
  description,
  sliderImages,
  collectionMethods,
  measurements,
  onAddToCartClick,
  onExpressPayClick,
  subscriptionOptions,
  onSubscriptionOptionChange,
  activeSubscriptionOption,
  purchaseTypeRadioButtons,
  onPurchaseTypeChange,
  activePurchaseType,
  onQuantityPickerChange,
  subscribeSelected,
  averageRating,
  reviewCount,
  topCallout,
  bottomCallout,
  descriptionCallout,
  modalButtonText,
  modalTitle,
  listOfMeasuredItems,
  hideSubscription,
  heroSubscriptionFaqUrl,
  onFrequencyTriggerClick,
  onSlide,
  ctaSubheadline,
  quantity,
  isOutOfStock,
  consultAvailable,
  consultAvailableText,
  consultIcon,
  promo,
  promotionable,
  onSlideChangeFromSwipe,
  onPrevArrowClick,
  onNextArrowClick,
  maxQuantity,
  onBulletClick,
  regionalRestrictionApplies,
}: PDPHeroProps): JSX.Element => {
  const [open, setOpen] = useState<boolean>(false);
  const heroRef = useRef<HTMLDivElement>(null);
  const [showSticky, setShowSticky] = useState(false);
  const { handleComparisonModalClick } = useContext(ComparisonModalContext);
  const { ref, inView, entry } = useInView({
    threshold: 0,
  });

  const isFoodSensitivityTest = name === 'Food Sensitivity Test';

  const userHasMembershipForTest = !!memberPrice;
  const final = memberPrice?.replace(/\D/g, '') || price;
  const stripeTotal = subscribeSelected
    ? activeSubscriptionOption.price
    : final;

  const handleMeasuresModalClick = () => {
    setOpen(true);
    ewAnalytics.track({
      event: ANALYTICS.EVENTS.CLICKED_BUTTON,
      data: {
        label: 'Hero Measures Modal Link',
      },
    });
  };

  ewAnalytics.useAttachTrackingToLinks(heroRef);

  useEffect(() => {
    if (entry) {
      if (!inView) {
        if (entry.boundingClientRect.bottom < 0) {
          setShowSticky(true && !isOutOfStock && !regionalRestrictionApplies);
        }
      } else {
        setShowSticky(false);
      }
    }
  }, [entry, inView, isOutOfStock, regionalRestrictionApplies]);

  return (
    <S.HeroWrapper ref={heroRef}>
      {topCallout ? (
        <S.Callout data-testid="topCallout">
          <span
            dangerouslySetInnerHTML={{
              __html: sanitize(topCallout),
            }}
          />
        </S.Callout>
      ) : null}
      {showSticky ? (
        <S.StickyOrderBarContainer data-testid="stickyAddToCart">
          <S.StickyContent>
            <S.StickyProductName>{name}</S.StickyProductName>
            <PurchaseControls
              purchaseTypeRadioButtons={purchaseTypeRadioButtons}
              onPurchaseTypeChange={onPurchaseTypeChange}
              selectedPurchaseType={activePurchaseType}
              subscriptionOptions={subscriptionOptions}
              onQuantityPickerChange={onQuantityPickerChange}
              quantity={quantity}
              onSubscriptionOptionChange={onSubscriptionOptionChange}
              selectedSubscriptionOption={activeSubscriptionOption}
              onAddToCartClick={onAddToCartClick}
              activeCredits={activeCredits}
              membershipVariantId={membershipVariantId}
              onFrequencyTriggerClick={onFrequencyTriggerClick}
              isStickyAppearance
              hideSubscription={hideSubscription}
              heroSubscriptionFaqUrl={heroSubscriptionFaqUrl}
              onExpressPayClick={onExpressPayClick}
              stripeTotal={stripeTotal}
              ctaSubheadline={ctaSubheadline}
              maxQuantity={maxQuantity}
            />
          </S.StickyContent>
        </S.StickyOrderBarContainer>
      ) : null}
      <S.Section data-testid="pdpHero">
        <Container>
          <Row>
            <Col xs={12} lg={6}>
              <S.ProductDescriptionHeaderMobile>
                {descriptionHeader}
              </S.ProductDescriptionHeaderMobile>
              <S.SliderContainer>
                <div data-testid={PDP.HERO_SWIPER}>
                  <PDPSwiperWrapper
                    sliderImages={sliderImages}
                    onSlideChange={onSlide}
                    onSlideChangeFromSwipe={onSlideChangeFromSwipe}
                    onNextArrowClick={onNextArrowClick}
                    onPrevArrowClick={onPrevArrowClick}
                    onBulletClick={onBulletClick}
                  ></PDPSwiperWrapper>
                </div>
              </S.SliderContainer>
            </Col>
            <Col xs={12} lg={6}>
              <S.Content>
                <S.ProductDescriptionHeader>
                  {descriptionHeader}
                </S.ProductDescriptionHeader>
                <S.InfoSection>
                  <S.ProductName data-testid="heroProductName">
                    {name}
                  </S.ProductName>
                  {reviewCount ? (
                    <AnchorLink offset={size.xl5} href="#reviewsSection">
                      <S.Stars
                        rating={averageRating}
                        numOfReviews={reviewCount}
                      />
                    </AnchorLink>
                  ) : null}
                  <S.DescriptionContent>
                    <S.ProductDescription>
                      <span
                        dangerouslySetInnerHTML={{
                          __html: sanitize(description),
                        }}
                      />
                    </S.ProductDescription>
                    {listOfMeasuredItems &&
                    listOfMeasuredItems.length &&
                    modalButtonText &&
                    modalTitle ? (
                      <>
                        <S.MeasuresWrapper>
                          <S.MeasuresText
                            iconUrl={measurements[0].iconUrl}
                            name={measurements[0].name}
                            isHTML
                            data-testid="heroMeasuresText"
                          />
                          <S.ModalButton onClick={handleMeasuresModalClick}>
                            {modalButtonText}
                          </S.ModalButton>
                        </S.MeasuresWrapper>
                        <MeasuredItemsModal
                          modalTitle={modalTitle}
                          listOfItems={listOfMeasuredItems}
                          open={open}
                          openHandler={setOpen}
                        />
                      </>
                    ) : (
                      <S.MeasuresWrapper>
                        <S.MeasuresText
                          iconUrl={measurements[0].iconUrl}
                          name={measurements[0].name}
                          isHTML
                          data-testid="heroMeasuresText"
                        />
                      </S.MeasuresWrapper>
                    )}
                    <S.CollectionMethods data-testid="heroCollectionMethods">
                      {[...collectionMethods].map((method) => (
                        <S.CollectionMethod
                          key={generateKey()}
                          iconUrl={method.iconUrl}
                          name={method.name}
                        />
                      ))}
                    </S.CollectionMethods>
                    {isFoodSensitivityTest ? (
                      <S.FscUpgradeLinkWrapper>
                        <S.MeasuresText
                          iconUrl={star}
                          name="Test a"
                          isHTML
                          data-testid="heroComparisonText"
                        />
                        <S.ModalButton onClick={handleComparisonModalClick}>
                          larger variety of foods
                        </S.ModalButton>
                      </S.FscUpgradeLinkWrapper>
                    ) : null}
                    {consultAvailable ? (
                      <S.ConsultWrapper>
                        <S.ConsultText
                          iconUrl={consultIcon}
                          name={consultAvailableText}
                          isHTML
                          data-testid="heroConsultAvailable"
                        />
                      </S.ConsultWrapper>
                    ) : null}
                  </S.DescriptionContent>
                </S.InfoSection>
                <Pricing
                  price={price}
                  activeSubscriptionOption={activeSubscriptionOption}
                  promo={promo}
                  productId={productId}
                  promotionable={promotionable}
                  memberPrice={memberPrice}
                  subscribeSelected={subscribeSelected}
                  memberHeroCopy={memberHeroCopy}
                  isOutOfStock={isOutOfStock}
                />
                {descriptionCallout && !userHasMembershipForTest ? (
                  <S.DescriptionCallout data-testid="descriptionCallout">
                    <span
                      dangerouslySetInnerHTML={{
                        __html: sanitize(descriptionCallout),
                      }}
                    />
                  </S.DescriptionCallout>
                ) : null}
                <S.PurchaseSection ref={ref}>
                  {isOutOfStock ? (
                    <S.ProductDescription data-testid="outOfStockMessage">
                      This test is currently unavailable.
                    </S.ProductDescription>
                  ) : (
                    <>
                      {regionalRestrictionApplies ? (
                        <S.ProductDescription data-testid="outOfStockMessage">
                          This test is not available in New York state.
                        </S.ProductDescription>
                      ) : (
                        <PurchaseControls
                          purchaseTypeRadioButtons={purchaseTypeRadioButtons}
                          onPurchaseTypeChange={onPurchaseTypeChange}
                          selectedPurchaseType={activePurchaseType}
                          subscriptionOptions={subscriptionOptions}
                          onQuantityPickerChange={onQuantityPickerChange}
                          quantity={quantity}
                          onSubscriptionOptionChange={
                            onSubscriptionOptionChange
                          }
                          selectedSubscriptionOption={activeSubscriptionOption}
                          onAddToCartClick={onAddToCartClick}
                          activeCredits={activeCredits}
                          membershipVariantId={membershipVariantId}
                          hideSubscription={hideSubscription}
                          heroSubscriptionFaqUrl={heroSubscriptionFaqUrl}
                          onFrequencyTriggerClick={onFrequencyTriggerClick}
                          onExpressPayClick={onExpressPayClick}
                          stripeTotal={stripeTotal}
                          ctaSubheadline={ctaSubheadline}
                          maxQuantity={maxQuantity}
                        />
                      )}
                    </>
                  )}
                </S.PurchaseSection>
              </S.Content>
            </Col>
          </Row>
        </Container>
        {bottomCallout ? (
          <Themable>
            <S.BottomCallout theme={Theme.Default} data-testid="bottomCallout">
              <span
                dangerouslySetInnerHTML={{
                  __html: sanitize(bottomCallout),
                }}
              />
            </S.BottomCallout>
            <S.BottomCallout
              theme={Theme.Winter2020}
              data-testid="bottomCallout"
            >
              <S.BottomCalloutImgL src={lightsLeft} />
              <span
                dangerouslySetInnerHTML={{
                  __html: sanitize(bottomCallout),
                }}
              />
              <S.BottomCalloutImgR src={lightsRight} />
            </S.BottomCallout>
          </Themable>
        ) : null}
      </S.Section>
    </S.HeroWrapper>
  );
};

export default PDPHero;
